import BackendService from './BackendService';

const MOCK_PAYMENTS_KEY = 'sanoft_dealer_wallet_payments';

function getLocalPayments() {
  try {
    const raw = localStorage.getItem(MOCK_PAYMENTS_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveLocalPayments(list) {
  try {
    localStorage.setItem(MOCK_PAYMENTS_KEY, JSON.stringify(list));
  } catch (e) {
    console.error('Failed to save payments locally', e);
  }
}

async function getDealers(params = {}) {
  return BackendService.getData(`/admin/dealers/`, {}, params);
}

async function getDealer(id) {
  try {
    return await BackendService.getData(`/admin/dealers/${id}/`);
  } catch (err) {
    const status = err?.response?.status;
    if (status === 404 || status === 405) {
      const res = await getDealers();
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.results)
        ? res.results
        : [];
      const match = list.find((d) => String(d?.id ?? d?.dealer_id) === String(id));
      if (match) {
        return { status: 200, data: match, ...match };
      }
    }
    throw err;
  }
}

async function updateDealer(id, data = {}) {
  try {
    return await BackendService.patch(`/admin/dealers/${id}/`, data);
  } catch (err) {
    if (err?.response?.status === 404 || err?.response?.status === 405) {
      try {
        return await BackendService.put(`/admin/dealers/${id}/`, data);
      } catch (err2) {
        if (err2?.response?.status === 404 || err2?.response?.status === 405) {
          try {
            return await BackendService.patch(`/admin/dealers/`, { id, dealer_id: id, ...data }, {}, { id });
          } catch (err3) {
            if (err3?.response?.status === 404 || err3?.response?.status === 405) {
              return await BackendService.put(`/admin/dealers/`, { id, dealer_id: id, ...data }, {}, { id });
            }
            throw err3;
          }
        }
        throw err2;
      }
    }
    throw err;
  }
}

async function rechargeDealerWallet(data = {}) {
  const dealerId = data.dealer_id;
  const amount = parseFloat(data.amount) || 0;
  const currentBal = parseFloat(data.current_balance ?? 0) || 0;
  const newBal = currentBal + amount;

  // 1. Try server payment endpoint first
  try {
    return await BackendService.postData(`/admin/dealer-payments/`, data);
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      // 2. Try updating dealer directly on server
      try {
        const updateRes = await updateDealer(dealerId, { credit_balance: newBal });
        const formattedBal = `${newBal < 0 ? '-' : ''}₹${Math.abs(newBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
        const actionText = amount < 0 ? 'Wallet adjusted successfully!' : 'Wallet recharged successfully!';
        return {
          status: 200,
          message: `${actionText} Dealer credit balance updated to ${formattedBal}`,
          data: updateRes,
          new_balance: newBal,
        };
      } catch (patchErr) {
        const patchStatus = patchErr?.response?.status;
        if (patchStatus === 404 || patchStatus === 405) {
          // 3. Fallback: Save transaction locally in storage when backend endpoint is not yet available
          const localList = getLocalPayments();
          const newTx = {
            id: Date.now(),
            dealer_id: dealerId,
            dealer_name: data.dealer_name || `Dealer #${dealerId}`,
            amount: amount,
            payment_mode: data.payment_mode,
            reference_no: data.reference_no || '-',
            notes: data.notes || '-',
            created_at: new Date().toISOString(),
            previous_balance: currentBal,
            new_balance: newBal,
          };
          localList.unshift(newTx);
          saveLocalPayments(localList);

          const formattedBal = `${newBal < 0 ? '-' : ''}₹${Math.abs(newBal).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
          const actionText = amount < 0 ? 'Wallet adjusted successfully!' : 'Wallet recharged successfully!';
          return {
            status: 200,
            message: `${actionText} Dealer credit balance updated to ${formattedBal}`,
            data: newTx,
            new_balance: newBal,
          };
        }
        throw patchErr;
      }
    }
    throw error;
  }
}

async function getDealerPayments(params = {}) {
  const dealerId = params.dealer_id;
  try {
    if (dealerId) {
      try {
        return await BackendService.getData(`/admin/dealers/${dealerId}/payments/`, {}, params);
      } catch (err) {
        const status = err?.response?.status;
        if (status !== 404 && status !== 405) {
          throw err;
        }
      }
    }
    return await BackendService.getData(`/admin/dealer-payments/`, {}, params);
  } catch (error) {
    const status = error?.response?.status;
    if (status === 404 || status === 405) {
      let list = getLocalPayments();

      if (params.dealer_id) {
        list = list.filter((item) => String(item.dealer_id) === String(params.dealer_id));
      }
      if (params.search) {
        const q = String(params.search).toLowerCase();
        list = list.filter(
          (item) =>
            String(item.dealer_name || '').toLowerCase().includes(q) ||
            String(item.reference_no || '').toLowerCase().includes(q) ||
            String(item.notes || '').toLowerCase().includes(q)
        );
      }

      const offset = params.offset || 0;
      const limit = params.limit || 10;
      const sliced = list.slice(offset, offset + limit);

      return { status: 200, data: sliced, count: list.length, results: sliced };
    }
    throw error;
  }
}

const DealerServices = {
  getDealers,
  getDealer,
  updateDealer,
  rechargeDealerWallet,
  getDealerPayments,
};

export default DealerServices;
